import { darken, lighten, ThemeOptions } from "@mui/material/styles";

import { getInvertColor } from "utils/themeUtils";
import { convertHexToRGBA } from "utils/commonUtils";

import { legacyColors } from "../colors";

export const buttonOverrides: ThemeOptions["components"] = {
	MuiButton: {
		defaultProps: {
			size: "s",
		},
		styleOverrides: {
			root: ({ ownerState: { fullWidth }, theme }) => ({
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				gap: theme.spacing(1),

				width: fullWidth ? "100%" : "auto",
				borderRadius: theme.spacing(2),

				":disabled": {
					pointerEvents: "all",
					cursor: "not-allowed",
				},
				"&:disabled .MuiSvg-root": {
					color: theme.palette.common.white,
				},
			}),

			sizeXs: ({ theme }) => ({
				paddingTop: theme.spacing(1.5),
				paddingBottom: theme.spacing(1.5),
				paddingLeft: theme.spacing(4),
				paddingRight: theme.spacing(4),
				minHeight: theme.spacing(9),
			}),

			sizeS: ({ theme }) => ({
				paddingTop: theme.spacing(2),
				paddingBottom: theme.spacing(2),
				paddingLeft: theme.spacing(4),
				paddingRight: theme.spacing(4),
				minHeight: theme.spacing(10),
			}),

			sizeM: ({ theme }) => ({
				paddingTop: theme.spacing(3),
				paddingBottom: theme.spacing(3),
				paddingLeft: theme.spacing(4),
				paddingRight: theme.spacing(4),
				minHeight: theme.spacing(12),
			}),

			sizeL: ({ theme }) => ({
				paddingTop: theme.spacing(4),
				paddingBottom: theme.spacing(4),
				paddingLeft: theme.spacing(4),
				paddingRight: theme.spacing(4),
				minHeight: theme.spacing(14),
			}),

			contained: ({ ownerState: { color }, theme }) => {
				const mainColor = theme.palette[`${color}`]?.main || theme.palette.primary.main;
				const isPrimaryOrSecondary = ["primary", "secondary"].includes(`${color}`);

				return {
					color: isPrimaryOrSecondary ? getInvertColor(mainColor) : theme.palette.common.white,

					"&:disabled": {
						color: theme.palette.common.white,
						backgroundColor: theme.palette.dirtyWhite["600"],
					},
					"&:not(:disabled):hover": {
						backgroundColor: darken(mainColor, 0.3),
					},
					"& .MuiSvg-root": {
						color: isPrimaryOrSecondary ? getInvertColor(mainColor) : theme.palette.common.white,
					},
					"&:disabled .MuiSvg-root": {
						color: theme.palette.common.white,
					},
				};
			},

			outlined: ({ ownerState: { color }, theme }) => {
				const mainColor = theme.palette[`${color}`]?.main || theme.palette.primary.main;
				const isLight = getInvertColor(mainColor) === theme.palette.dirtyWhite["2000"];
				const isPrimaryOrSecondary = ["primary", "secondary"].includes(`${color}`);

				return {
					border: "1px solid",
					borderColor: isPrimaryOrSecondary && isLight ? darken(mainColor, 0.5) : mainColor,
					color: isPrimaryOrSecondary && isLight ? darken(mainColor, 0.5) : mainColor,

					"&:hover, &:disabled": {
						borderWidth: "1px",
					},
					"&:disabled, &:disabled:hover": {
						borderColor: theme.palette.dirtyWhite["600"],
						color: theme.palette.dirtyWhite["600"],
						backgroundColor: "transparent",
					},
					"& .MuiSvg-root": {
						color: isPrimaryOrSecondary && isLight ? darken(mainColor, 0.5) : mainColor,
					},
					"&:disabled .MuiSvg-root": {
						color: theme.palette.dirtyWhite["600"],
					},
				};
			},

			text: ({ ownerState: { color }, theme }) => {
				const mainColor = theme.palette[`${color}`]?.main || theme.palette.primary.main;
				const isLight = getInvertColor(mainColor) === theme.palette.dirtyWhite["2000"];
				const isPrimaryOrSecondary = ["primary", "secondary"].includes(`${color}`);

				return {
					color: theme.palette.dirtyWhite["800"],

					"&:disabled": {
						color: theme.palette.dirtyWhite["600"],
						backgroundColor: "transparent",
					},
					"&:not(:disabled):hover": {
						color: isPrimaryOrSecondary && isLight ? darken(mainColor, 0.5) : mainColor,
					},
					"& .MuiSvg-root": {
						color: theme.palette.dirtyWhite["800"],
					},
					"&:disabled .MuiSvg-root": {
						color: theme.palette.dirtyWhite["600"],
					},
					"&:not(:disabled):hover .MuiSvg-root": {
						color: isPrimaryOrSecondary && isLight ? darken(mainColor, 0.5) : mainColor,
					},
				};
			},

			ghost: ({ ownerState: { color }, theme }) => {
				const mainColor = theme.palette[`${color}`]?.main || theme.palette.primary.main;
				const isLight = getInvertColor(mainColor) === theme.palette.dirtyWhite["2000"];
				const isPrimaryOrSecondary = ["primary", "secondary"].includes(`${color}`);

				return {
					color: isPrimaryOrSecondary && isLight ? darken(mainColor, 0.5) : mainColor,
					backgroundColor: lighten(mainColor, 0.9),

					"&:disabled": {
						color: theme.palette.common.white,
						backgroundColor: theme.palette.dirtyWhite["600"],
					},
					"&:not(:disabled):hover": {
						backgroundColor: lighten(mainColor, 0.7),
					},
					"& .MuiSvg-root": {
						color: isPrimaryOrSecondary && isLight ? darken(mainColor, 0.5) : mainColor,
					},
					"&:disabled .MuiSvg-root": {
						color: theme.palette.common.white,
					},
				};
			},

			approve: () => ({
				color: "#3C4255",
				backgroundColor: convertHexToRGBA(legacyColors.primary.main, 0.15),
				border: `1px solid ${legacyColors.primary.main}`,

				":hover": {
					backgroundColor: convertHexToRGBA(legacyColors.primary.main, 0.3),
				},
				":hover, :disabled": {
					border: `1px solid ${legacyColors.primary.main}`,
				},
				"& svg:not(.MuiSvg-root) path": {
					fill: "#3C4255",
				},
				"& .MuiSvg-root": {
					color: "#3C4255",
				},
			}),

			reject: ({ theme }) => ({
				color: "#3C4255",
				backgroundColor: theme.palette.common.white,
				border: `1px solid #F34236`,

				":hover": {
					backgroundColor: theme.palette.common.white,
				},
				":hover, :disabled": {
					border: `1px solid #F34236`,
				},
				"& svg:not(.MuiSvg-root) path": {
					fill: "#3C4255",
				},
				"& .MuiSvg-root": {
					color: "#3C4255",
				},
			}),

			complete: ({ theme }) => ({
				color: "#3C4255",
				backgroundColor: "#C4E4C5",
				// @ts-ignore
				border: `1px solid ${theme.palette.legacySuccess.main}`,

				":hover": {
					backgroundColor: darken("#C4E4C5", 0.3),
				},
				":hover, :disabled": {
					// @ts-ignore
					border: `1px solid ${theme.palette.legacySuccess.main}`,
				},
				"& svg:not(.MuiSvg-root) path": {
					fill: "#3C4255",
				},
				"& .MuiSvg-root": {
					color: "#3C4255",
				},
			}),

			startIcon: ({ ownerState, theme }) => {
				let fontSize;
				switch (ownerState.size) {
					case "xs":
						fontSize = theme.typography.pxToRem(16);
						break;
					case "s":
						fontSize = theme.typography.pxToRem(16);
						break;
					case "small":
						fontSize = theme.typography.pxToRem(16);
						break;
					case "m":
						fontSize = theme.typography.pxToRem(20);
						break;
					case "l":
						fontSize = theme.typography.pxToRem(24);
						break;
					case "large":
						fontSize = theme.typography.pxToRem(24);
						break;
					default:
						fontSize = theme.typography.pxToRem(20); // medium
				}

				return {
					marginRight: 0,

					"& > *:nth-of-type(1)": {
						fontSize,
					},
				};
			},

			endIcon: ({ ownerState, theme }) => {
				let fontSize;
				switch (ownerState.size) {
					case "xs":
						fontSize = theme.typography.pxToRem(16);
						break;
					case "s":
						fontSize = theme.typography.pxToRem(16);
						break;
					case "small":
						fontSize = theme.typography.pxToRem(16);
						break;
					case "m":
						fontSize = theme.typography.pxToRem(20);
						break;
					case "l":
						fontSize = theme.typography.pxToRem(24);
						break;
					case "large":
						fontSize = theme.typography.pxToRem(24);
						break;
					default:
						fontSize = theme.typography.pxToRem(20); // medium
				}

				return {
					marginLeft: 0,

					"& > *:nth-of-type(1)": {
						fontSize,
					},
				};
			},
		},
	},
};
